import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ArrowUpRight, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { EASE } from "./Motion";

const BLOCKED_PATHS = ["/preordina", "/pagamento", "/admin", "/hospitality", "/guide"];

function track(event, extra = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...extra });
}

export function FinancePopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [config, setConfig] = useState(null);
  const [current, setCurrent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const queueRef = useRef([]);
  const timerRef = useRef(null);
  const prevFocusRef = useRef(null);
  const dialogRef = useRef(null);

  const blocked = BLOCKED_PATHS.some((p) => location.pathname.startsWith(p));
  const offers = config?.offers || [];

  useEffect(() => {
    api.get("/finance").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const schedule = useCallback((id, seconds) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => maybeShowRef.current(id), Math.max(1, seconds) * 1000);
  }, []);

  const maybeShow = useCallback(
    (id) => {
      if (!offers.length) return;
      if (document.querySelector('[role="dialog"]')) { schedule(id, 5); return; }
      const ae = document.activeElement;
      if (ae && ["INPUT", "TEXTAREA", "SELECT"].includes(ae.tagName)) { schedule(id, 8); return; }
      if (BLOCKED_PATHS.some((p) => window.location.pathname.startsWith(p))) return;
      if (sessionStorage.getItem(`sw_fp_${id}`)) return;
      const offer = offers.find((o) => o.id === id);
      if (!offer) return;
      prevFocusRef.current = document.activeElement;
      setCurrent(offer);
      setDetailsOpen(false);
      track("finance_popup_view", { offer: id });
      track(id === "a" ? "finance_popup_offer_a" : "finance_popup_offer_b");
      window.dispatchEvent(new CustomEvent("swapa:finance", { detail: { open: true } }));
    },
    [offers, schedule]
  );
  const maybeShowRef = useRef(maybeShow);
  maybeShowRef.current = maybeShow;

  const dismiss = useCallback(
    (offer, { converted = false } = {}) => {
      if (!offer) return;
      track("finance_popup_close", { offer: offer.id, converted });
      sessionStorage.setItem(`sw_fp_${offer.id}`, "1");
      window.dispatchEvent(new CustomEvent("swapa:finance", { detail: { open: false } }));
      setCurrent(null);
      prevFocusRef.current?.focus?.();
      const rest = queueRef.current.filter((q) => q !== offer.id && !sessionStorage.getItem(`sw_fp_${q}`));
      queueRef.current = rest;
      if (rest.length && !converted) schedule(rest[0], config?.interval_second ?? 10);
    },
    [config, schedule]
  );

  useEffect(() => {
    if (!config || blocked || current) return;
    const parity = localStorage.getItem("sw_fp_parity") === "b" ? "b" : "a";
    localStorage.setItem("sw_fp_parity", parity === "a" ? "b" : "a");
    const order = parity === "a" ? ["a", "b"] : ["b", "a"];
    const remaining = order.filter((id) => !sessionStorage.getItem(`sw_fp_${id}`) && offers.find((o) => o.id === id));
    queueRef.current = remaining;
    if (remaining.length) schedule(remaining[0], config.interval_first ?? 5);
    return () => clearTimeout(timerRef.current);
  }, [config, blocked]);

  useEffect(() => {
    if (!current) return;
    const dlg = dialogRef.current;
    if (!dlg) return;
    const focusables = () => Array.from(dlg.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'));
    focusables()[0]?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss(current);
      } else if (e.key === "Tab") {
        const f = focusables();
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, dismiss]);

  const goOffer = () => {
    track("finance_popup_cta", { offer: current.id, cta: "scopri" });
    dismiss(current, { converted: true });
    navigate(`/finanziamento-swapa-zip?offerta=${current.id}`);
  };

  const goTestDrive = () => {
    track("finance_popup_cta", { offer: current.id, cta: "test_drive" });
    dismiss(current, { converted: true });
    navigate("/test-drive");
  };

  const details = current
    ? [
        ["Primo canone", `€${current.anticipo}`],
        [`${current.rate} rate da`, `€${current.monthly}`],
        ["TAN fisso", current.tan],
        ["TAEG", current.taeg],
        ["Rata finale", `€${current.rata_finale}`],
      ]
    : [];

  return (
    <AnimatePresence>
      {current && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center sm:justify-center" role="presentation">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            aria-hidden="true"
          />
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-popup-title"
            data-testid="finance-popup"
            initial={reduce ? false : { opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative max-h-[82dvh] w-full overflow-y-auto border-t border-white/15 bg-card shadow-[0_30px_80px_rgba(0,0,0,0.6)] sm:max-h-none sm:w-[min(920px,92vw)] sm:overflow-visible sm:border"
          >
            <button
              onClick={() => dismiss(current)}
              data-testid="finance-popup-close"
              aria-label="Chiudi finestra finanziamento"
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center border border-white/20 bg-black/60 text-white backdrop-blur transition-colors hover:border-swapa-yellow hover:text-swapa-yellow"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="grid sm:grid-cols-[1.1fr_1fr]">
              <div className="relative h-40 overflow-hidden sm:h-auto sm:min-h-[460px]">
                <img
                  src={current.image || "/assets/colours/aguyl-pink.webp"}
                  alt="SWAPA in offerta finanziaria"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent sm:bg-gradient-to-r" aria-hidden="true" />
              </div>

              <div className="flex flex-col p-6 sm:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-swapa-yellow">
                  Swapa Zip<br />Finance
                </p>
                <h2 id="finance-popup-title" className="mt-5 font-display font-extrabold uppercase leading-[0.92] tracking-tight text-white">
                  <span className="block text-sm font-bold tracking-[0.2em] text-zinc-400">Tua da</span>
                  <span className="mt-1 block font-mono text-5xl font-semibold tracking-tighter text-white sm:text-6xl">
                    €{current.monthly}
                  </span>
                  <span className="mt-1 block text-sm font-bold tracking-[0.2em] text-zinc-400">/ mese</span>
                </h2>
                <p className="mt-4 font-display text-lg font-bold uppercase tracking-tight text-swapa-yellow">
                  {current.anticipo_label}
                </p>

                <div className="mt-5 hidden grid-cols-2 gap-x-6 gap-y-2 border-t border-white/10 pt-5 sm:grid">
                  {details.map(([k, v]) => (
                    <p key={k} className="flex items-baseline justify-between gap-2 font-mono text-[11px] text-zinc-400">
                      <span>{k}</span>
                      <span className="text-white">{v}</span>
                    </p>
                  ))}
                </div>
                {current.refi && (
                  <p className="mt-3 hidden font-serif text-sm italic text-zinc-400 sm:block">
                    oppure {current.refi} per rifinanziare la rata finale*
                  </p>
                )}

                <div className="mt-3 sm:hidden">
                  <button
                    data-testid="finance-details-toggle"
                    onClick={() => setDetailsOpen(!detailsOpen)}
                    aria-expanded={detailsOpen}
                    className="flex w-full items-center justify-between border-t border-white/10 pt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300"
                  >
                    Dettagli finanziamento
                    <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${detailsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {detailsOpen && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-3">
                      {details.map(([k, v]) => (
                        <p key={k} className="flex items-baseline justify-between gap-2 font-mono text-[11px] text-zinc-400">
                          <span>{k}</span>
                          <span className="text-white">{v}</span>
                        </p>
                      ))}
                      {current.refi && (
                        <p className="col-span-2 pt-1 font-serif text-xs italic text-zinc-400">
                          oppure {current.refi} per rifinanziare la rata finale*
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    onClick={goOffer}
                    data-testid="finance-popup-cta-primary"
                    className="group inline-flex items-center justify-center gap-2 bg-swapa-yellow px-6 py-3.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    {current.cta || "Scopri l'offerta"}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                  <button
                    onClick={goTestDrive}
                    data-testid="finance-popup-cta-secondary"
                    className="inline-flex items-center justify-center gap-2 border border-white/25 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-white transition-colors hover:border-swapa-yellow hover:text-swapa-yellow"
                  >
                    Prenota un test drive
                  </button>
                </div>

                <p className="mt-5 text-[10px] leading-relaxed text-zinc-500">
                  {current.legal?.trim()
                    ? current.legal
                    : "Messaggio pubblicitario con finalità promozionale. Offerta di finanziamento soggetta ad approvazione e valutazione del merito creditizio da parte dell'istituto finanziatore. *Condizioni di rifinanziamento della rata finale soggette a nuova approvazione. La documentazione di trasparenza completa (esempio rappresentativo, importo totale del credito e importo totale dovuto) è in corso di pubblicazione e disponibile presso il venditore."}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
